import i18n from '@whatsmenu/i18n'
import { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'

/**
 * Ambiente de reimpressão do lojista
 * O ambiente de id 1 é o Caixa e vem selecionado por padrão.
 */
export type PrintEnvironment = {
  id: number
  type: 'fiscal' | 'production'
  name: string
}

/** Ambiente padrão "Caixa"  */
export const CASHIER_ENV_ID = 1

type ReprintEnvsModalProps = {
  show: boolean
  /** Confirma a reimpressão com os ambientes escolhidos. */
  onConfirm: (selectedLocationIds: number[]) => void
  /** Cancela a reimpressão mantendo os dados anteriores. */
  onCancel: () => void
}

/**
 * Modal de reimpressão (somente desktop/Electron)
 *
 * Pergunta ao lojista para quais ambientes deseja reimprimir
 * Lista o Caixa já marcado por padrão e os demais ambientes cadastrados
 * Ao confirmar, dispara a reimpressão nos ambientes escolhidos
 * Ao cancelar, nenhuma impressão é feita e o estado anterior é preservado
 */
export function ReprintEnvsModal({
  show,
  onConfirm,
  onCancel,
}: ReprintEnvsModalProps) {
  const [environments, setEnvironments] = useState<PrintEnvironment[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([CASHIER_ENV_ID])

  // Busca os ambientes toda vez que o modal abre e sincroniza as seleções salvas.
  useEffect(() => {
    const desktopApi = (window as any).DesktopApi
    if (!show || !('isElectron' in window) || !desktopApi) {
      return
    }

    const handleLocations = (
      _event: unknown,
      locations: PrintEnvironment[]
    ) => {
      const list = Array.isArray(locations) ? locations : []
      setEnvironments(list)
      // Carrega a última seleção salva
      const applySelection = (stored: number[]) => {
        const storedIds = Array.isArray(stored) ? stored : []
        const valid = storedIds.filter((id) =>
          list.some((env) => env.id === id)
        )
        setSelectedIds(valid)
      }

      if (typeof desktopApi.getReprintLocations === 'function') {
        desktopApi
          .getReprintLocations()
          .then(applySelection)
          .catch(() => applySelection([CASHIER_ENV_ID]))
      } else {
        applySelection([CASHIER_ENV_ID])
      }
    }

    desktopApi.onPrinterLocationsChange?.(handleLocations)
    desktopApi.getPrinterLocations?.()

    return () => {
      desktopApi.offPrinterLocationsChange?.(handleLocations)
    }
  }, [show])

  const toggleEnvironment = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((envId) => envId !== id) : [...prev, id]
    )
  }

  const handleConfirm = () => {
    // Persiste a escolha antes de reimprimir
    const desktopApi = (window as any).DesktopApi
    if (desktopApi && typeof desktopApi.setReprintLocations === 'function') {
      try {
        desktopApi.setReprintLocations(selectedIds)
      } catch {
        console.error('Não deve ocorrer a impressão')
      }
    }
    onConfirm(selectedIds)
  }

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      contentClassName="border-0 rounded-4 shadow-lg overflow-hidden"
      style={{ zIndex: 100000 }}
      onHide={onCancel}
    >
      <Modal.Header closeButton className="border-0 px-4 pb-2 pt-4">
        <Modal.Title className="fs-5 fw-bold text-dark">
          Reimpressão de Pedidos
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pb-4 pt-2">
        <p className="text-secondary mb-4">
          Para quais ambientes deseja reimprimir?
        </p>
        <Form className="d-grid gap-2">
          {environments.length === 0 ? (
            <p className="text-muted bg-light rounded-3 mb-0 p-4 text-center">
              Nenhum ambiente de impressão configurado.
            </p>
          ) : (
            environments.map((environment) => (
              <label
                key={environment.id}
                htmlFor={`reprint-env-${environment.id}`}
                className={`d-flex align-items-center rounded-3 mb-0 gap-3 border p-3 transition ${
                  selectedIds.includes(environment.id)
                    ? 'border-primary bg-light shadow-sm'
                    : 'border-light-subtle'
                }`}
                style={{ cursor: 'pointer' }}
              >
                <Form.Check.Input
                  type="checkbox"
                  id={`reprint-env-${environment.id}`}
                  className="position-static m-0 flex-shrink-0"
                  checked={selectedIds.includes(environment.id)}
                  onChange={() => toggleEnvironment(environment.id)}
                />
                <span>{environment.name}</span>
              </label>
            ))
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-light border-0 px-4 py-3">
        <Button variant="outline-secondary" className="px-3" onClick={onCancel}>
          {i18n.t('cancel')}
        </Button>
        <Button
          variant="primary"
          className="fw-semibold px-4"
          disabled={selectedIds.length === 0}
          onClick={handleConfirm}
        >
          {i18n.t('print')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
